import { useMemo, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Archive,
  BarChart3,
  CheckSquare2,
  ChevronsDownUp,
  Download,
  Link2,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { SHARED_FOLDER_COLORS } from "@hydra-notes/shared";
import { useAllSpaceItems, resolveItem } from "@/data/store";
import { useAllTasks, useTaskLists, type TaskListMeta } from "@/data/taskHooks";
import { useUi } from "@/store/ui";
import { useSharedFolders } from "@/data/sharedFolders";
import { archiveItem, canEditIn, roleIn, trashItem } from "@/commands/nav";
import { createTaskList, createTaskListIn, openTasksDashboard } from "@/commands/tasks";
import { useArchiveDialog } from "@/components/ArchiveDialog";
import { useTrashDialog } from "@/components/TrashDialog";
import { IconButton } from "@/ui/IconButton";
import s from "./TasksSidebar.module.css";

export function TasksSidebar({ onClose }: { onClose?: () => void }) {
  const lists = useTaskLists();
  const { rows } = useAllTasks();
  const spaces = useAllSpaceItems();
  const sharedFolders = useSharedFolders((state) => state.folders);
  const active = useUi((state) => state.activeTaskView);
  const toggleSidebar = useUi((state) => state.toggleTree);
  const openArchive = useArchiveDialog((state) => state.setOpen);
  const openTrash = useTrashDialog((state) => state.setOpen);

  const counts = useMemo(() => {
    const next = new Map<string, number>();
    for (const row of rows) if (!row.task.done) next.set(row.list.listId, (next.get(row.list.listId) ?? 0) + 1);
    return next;
  }, [rows]);
  const personal = lists.filter((list) => list.spaceKind === "personal");
  const shared = lists.filter((list) => list.spaceKind === "shared");
  const writableShared = sharedFolders.filter((folder) => !folder.locked && folder.role !== "viewer");
  const archived = spaces.flatMap((space) => space.items).filter((item) => item.type === "tasklist" && item.archivedAt && !item.trashedAt).length;
  const trashed = spaces.flatMap((space) => space.items).filter((item) => item.type === "tasklist" && item.trashedAt).length;

  return (
    <div className={s.sidebar} aria-label="Tasks navigation">
      <header className={s.header}>
        <span>Tasks</span>
        <IconButton label="New task list" onClick={() => void createTaskList()}><Plus size={14} /></IconButton>
        {writableShared.length ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <span><IconButton label="New shared task list"><Users size={14} /></IconButton></span>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content className="hn-menu" side="right" align="start" sideOffset={5}>
                <DropdownMenu.Label className="hn-menu-label">Create in shared folder</DropdownMenu.Label>
                {writableShared.map((folder) => (
                  <DropdownMenu.Item
                    key={folder.id}
                    className="hn-menu-item"
                    onSelect={() => void createTaskListIn(null, folder.id)}
                  >
                    <Users /> {folder.title}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ) : null}
        <IconButton label="Hide sidebar" onClick={onClose ?? toggleSidebar}><ChevronsDownUp size={14} /></IconButton>
      </header>

      <nav className={s.nav}>
        <button type="button" className={[s.overview, active === "overview" ? s.active : ""].join(" ")} onClick={openTasksDashboard}>
          <BarChart3 size={15} />
          <span>Overview</span>
          <span className={s.total}>{rows.filter((row) => !row.task.done).length}</span>
        </button>

        <ListSection label="My lists" lists={personal} counts={counts} active={active} />
        {shared.length ? <ListSection label="Shared lists" lists={shared} counts={counts} active={active} /> : null}
      </nav>

      <footer className={s.footer}>
        <button type="button" onClick={() => openArchive(true)}><Archive size={13} />Archive{archived ? ` (${archived})` : ""}</button>
        <button type="button" onClick={() => openTrash(true)}><Trash2 size={13} />Trash{trashed ? ` (${trashed})` : ""}</button>
      </footer>
    </div>
  );
}

function ListSection({
  label,
  lists,
  counts,
  active,
}: {
  label: string;
  lists: ReturnType<typeof useTaskLists>;
  counts: Map<string, number>;
  active: string;
}) {
  if (!lists.length) return null;
  return (
    <section className={s.section}>
      <h2>{label}</h2>
      {lists.map((list) => (
        <TaskListRow
          key={`${list.spaceId}/${list.listId}`}
          list={list}
          count={counts.get(list.listId) ?? 0}
          active={active === list.listId}
        />
      ))}
    </section>
  );
}

function TaskListRow({ list, count, active }: { list: TaskListMeta; count: number; active: boolean }) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(list.title);

  const commitRename = () => {
    const next = title.trim();
    const resolved = resolveItem(list.listId);
    if (next && resolved && canEditIn(resolved.ctx)) resolved.ctx.workspace.rename(list.listId, next);
    else setTitle(list.title);
    setRenaming(false);
  };

  return (
    <div className={[s.listRow, active ? s.active : ""].join(" ")}>
      {renaming ? (
        <div className={s.renameRow}>
          <CheckSquare2 size={14} style={{ color: `var(--tl-${list.colorKey ?? "gray"})` }} />
          <input
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onFocus={(event) => event.target.select()}
            onBlur={commitRename}
            onKeyDown={(event) => {
              if (event.key === "Enter") commitRename();
              if (event.key === "Escape") {
                setTitle(list.title);
                setRenaming(false);
              }
            }}
          />
        </div>
      ) : (
        <button type="button" className={s.listOpen} onClick={() => useUi.getState().openTaskView(list.listId)}>
          <CheckSquare2 size={14} style={{ color: `var(--tl-${list.colorKey ?? "gray"})` }} />
          <span>{list.title}</span>
          <small>{count}</small>
        </button>
      )}
      {!renaming ? <TaskListMenu listId={list.listId} builtin={list.builtin} onRename={() => setRenaming(true)} /> : null}
    </div>
  );
}

function TaskListMenu({ listId, builtin, onRename }: { listId: string; builtin: boolean; onRename: () => void }) {
  const resolved = resolveItem(listId);
  if (!resolved || !canEditIn(resolved.ctx)) return null;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <span className={s.menuTrigger}><IconButton label="Task list actions"><MoreHorizontal size={13} /></IconButton></span>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="hn-menu" side="right" align="start" sideOffset={5}>
          <DropdownMenu.Item className="hn-menu-item" onSelect={onRename}>
            <Pencil /> Rename
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="hn-menu-sep" />
          <DropdownMenu.Label className="hn-menu-label">Color</DropdownMenu.Label>
          <div className={s.colors}>
            {SHARED_FOLDER_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                aria-label={`${color} task list`}
                className={s.color}
                style={{ background: `var(--tl-${color})` }}
                onClick={() => resolved.ctx.workspace.setColorKey(listId, color === "gray" ? undefined : color)}
              />
            ))}
          </div>
          <DropdownMenu.Separator className="hn-menu-sep" />
          <DropdownMenu.Item
            className="hn-menu-item"
            onSelect={() => {
              void import("@/discord/DiscordLinkDialog").then(({ useDiscordLinkDialog }) =>
                useDiscordLinkDialog.getState().openForList({
                  listId,
                  ...(resolved.ctx.kind === "shared" ? { spaceId: resolved.ctx.workspaceId } : {}),
                }),
              );
            }}
          >
            <Link2 /> Discord board
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="hn-menu-item"
            onSelect={() => {
              void import("@/vikunja/VikunjaImportDialog").then(({ useVikunjaDialog }) =>
                useVikunjaDialog.getState().openImport({
                  listId,
                  ...(resolved.ctx.kind === "shared" ? { spaceId: resolved.ctx.workspaceId } : {}),
                }),
              );
            }}
          >
            <Download /> Import from Vikunja
          </DropdownMenu.Item>
          {!builtin ? (
            <>
              <DropdownMenu.Separator className="hn-menu-sep" />
              {resolved.ctx.kind === "personal" ? (
                <DropdownMenu.Item className="hn-menu-item" onSelect={() => archiveItem(listId)}>
                  <Archive /> Archive
                </DropdownMenu.Item>
              ) : null}
              {resolved.ctx.kind === "personal" || roleIn(resolved.ctx) === "owner" ? (
                <DropdownMenu.Item className="hn-menu-item danger" onSelect={() => trashItem(listId)}>
                  <Trash2 /> Move to trash
                </DropdownMenu.Item>
              ) : null}
            </>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
